/**
 * Convert orientation object to radians
 * @param {Object} orientation - Orientation object with type and value
 * @returns {number} Angle in radians
 */
export function convertOrientationToRadians(orientation) {
  // Default to 180 degrees (downward) if no orientation
  if (!orientation || !orientation.value) {
    return Math.PI;
  }

  const angle = orientation.value;

  // Handle keyword directions
  if (typeof angle === 'string') {
    const bottom = angle.indexOf('bottom') > -1;
    const top = angle.indexOf('top') > -1;
    const left = angle.indexOf('left') > -1;
    const right = angle.indexOf('right') > -1;

    if (bottom) {
      if (left) return 1.25 * Math.PI; // 225 degrees
      if (right) return 0.75 * Math.PI; // 135 degrees
      return Math.PI; // 180 degrees
    }
    if (top) {
      if (left) return 1.75 * Math.PI; // 315 degrees
      if (right) return 0.25 * Math.PI; // 45 degrees
      return 0; // 0 degrees
    }
    if (left) return 1.5 * Math.PI; // 270 degrees
    if (right) return 0.5 * Math.PI; // 90 degrees
  }

  // Handle numeric angles with different units
  const numericAngle = parseFloat(angle);

  switch (orientation.type) {
    case 'grad':
      return (numericAngle * Math.PI) / 200;
    case 'rad':
      return numericAngle;
    case 'turn':
      return numericAngle * 2 * Math.PI;
    default:
      // Default to degrees
      return (numericAngle * Math.PI) / 180;
  }
}

/**
 * Convert CSS gradient direction keywords and angle units to radians
 * CSS gradient directions (per CSS Images Module Level 3):
 *   to top = 0deg, to right = 90deg, to bottom = 180deg, to left = 270deg
 *   to top right = 45deg, to bottom right = 135deg
 *   to bottom left = 225deg, to top left = 315deg
 * @param {string} angle - CSS angle string
 * @returns {number} Angle in radians
 */
export function convertAngleToRadians(angle) {
  let convertedAngle = parseFloat(angle);

  // Handle CSS direction keywords
  if (angle.indexOf('to') > -1) {
    const bottom = angle.indexOf('bottom') > -1;
    const top = angle.indexOf('top') > -1;
    const left = angle.indexOf('left') > -1;
    const right = angle.indexOf('right') > -1;

    if (bottom) {
      if (left) {
        convertedAngle = 225; // to bottom left
      } else if (right) {
        convertedAngle = 135; // to bottom right
      } else {
        convertedAngle = 180; // to bottom
      }
    } else if (top) {
      if (left) {
        convertedAngle = 315; // to top left
      } else if (right) {
        convertedAngle = 45; // to top right
      } else {
        convertedAngle = 0; // to top
      }
    } else if (left) {
      convertedAngle = 270; // to left
    } else if (right) {
      convertedAngle = 90; // to right
    }
    convertedAngle = (convertedAngle * Math.PI) / 180;
  } else if (angle.indexOf('deg') > -1) {
    // degrees: 180deg = π radians
    convertedAngle = (convertedAngle * Math.PI) / 180;
  } else if (angle.indexOf('grad') > -1) {
    // gradians: 200grad = π radians
    convertedAngle = (convertedAngle * Math.PI) / 200;
  } else if (angle.indexOf('turn') > -1) {
    // turns: 1turn = 2π radians
    convertedAngle = convertedAngle * 2 * Math.PI;
  } else if (isNaN(convertedAngle)) {
    // no valid value, default to 180deg (to bottom)
    convertedAngle = Math.PI;
  }

  return convertedAngle;
}

/**
 * Prepare CSS angle for gradient calculation
 * CSS linear-gradient default direction is "to bottom" (180deg)
 * @param {string|Object} angle - CSS angle or orientation object
 * @returns {Object} Object with theta and revTheta
 */
export function prepareCssAngle(angle = 'to bottom') {
  let theta;

  if (angle?.value) {
    theta = Math.PI / 2 - convertOrientationToRadians(angle);
  } else {
    theta = Math.PI / 2 - convertAngleToRadians(angle);
  }

  const revTheta = theta + Math.PI;

  return {
    theta,
    revTheta,
  };
}

/**
 * Calculate edge point on rectangle at given angle
 * @see http://stackoverflow.com/questions/4061576/finding-points-on-a-rectangle-at-a-given-angle
 * Angle is in radians, starting position is the right side of the rectangle
 * Positive degrees move counter-clockwise
 * @param {number} rectWidth - Rectangle width
 * @param {number} rectHeight - Rectangle height
 * @param {number} angle - Angle in radians
 * @returns {Object} Object with x, y coordinates and region
 */
export function getRectAngleBound(rectWidth, rectHeight, angle) {
  const twoPI = Math.PI * 2;
  let theta = angle;

  // Normalize angle to [-π, π]
  while (theta < -Math.PI) {
    theta += twoPI;
  }
  while (theta > Math.PI) {
    theta -= twoPI;
  }

  const rectAtan = Math.atan2(rectHeight, rectWidth);
  const tanTheta = Math.tan(theta);
  let region;

  if (theta > -rectAtan && theta <= rectAtan) {
    region = 1; // right
  } else if (theta > rectAtan && theta <= Math.PI - rectAtan) {
    region = 2; // top
  } else if (theta > Math.PI - rectAtan || theta <= -(Math.PI - rectAtan)) {
    region = 3; // left
  } else {
    region = 4; // bottom
  }

  const edgePoint = { x: rectWidth / 2, y: rectHeight / 2 };
  const cx = rectWidth / 2;
  const cy = rectHeight / 2;

  switch (region) {
    case 1: // right
      edgePoint.x = rectWidth;
      edgePoint.y = cy - (rectWidth / 2) * tanTheta;
      break;
    case 2: // top
      edgePoint.x = cx + rectHeight / (2 * tanTheta);
      edgePoint.y = 0;
      break;
    case 3: // left
      edgePoint.x = 0;
      edgePoint.y = cy + (rectWidth / 2) * tanTheta;
      break;
    case 4: // bottom
      edgePoint.x = cx - rectHeight / (2 * tanTheta);
      edgePoint.y = rectHeight;
      break;
  }

  return {
    x: edgePoint.x,
    y: edgePoint.y,
    region,
  };
}
